// Schedule Inspection Panel
// Pull-out panel for scheduling future inspections

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Form validation schema
const formSchema = z.object({
  scheduledDate: z.date({
    message: 'Scheduled date is required',
  }),
  assignedTo: z.string().min(1, 'Assigned inspector is required'),
  priority: z.enum(['Low', 'Medium', 'High'], {
    message: 'Priority is required',
  }),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceItem {
  id: number;
  assetName: string;
  regimeName: string;
  dueDate?: string;
}

interface ScheduleInspectionPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: ComplianceItem;
  onSubmit: (data: FormValues) => Promise<void>;
}

export function ScheduleInspectionPanel({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: ScheduleInspectionPanelProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      scheduledDate: undefined,
      assignedTo: '',
      priority: 'Medium',
      notes: '',
    },
  });

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error scheduling inspection:', error);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="text-lg font-semibold">Schedule Inspection</SheetTitle>
          <SheetDescription>
            Schedule a future inspection for this compliance requirement
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <Form {...form}>
            <form id="schedule-inspection-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 pb-6">
              {/* Compliance Item Info */}
              {complianceItem && (
                <div className="bg-muted rounded-lg p-4">
                  <h3 className="text-base font-semibold mb-3">Compliance Item</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground font-semibold">Asset</Label>
                      <p className="mt-1">{complianceItem.assetName}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground font-semibold">Regulation</Label>
                      <p className="mt-1">{complianceItem.regimeName}</p>
                    </div>
                    {complianceItem.dueDate && (
                      <div className="col-span-2">
                        <Label className="text-muted-foreground font-semibold">Due Date</Label>
                        <p className="mt-1">
                          {format(new Date(complianceItem.dueDate), 'PPP')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Inspection Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Scheduled Date */}
                  <FormField
                    control={form.control}
                    name="scheduledDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="font-semibold">Scheduled Date *</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal bg-background',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription className="text-xs">
                          When should the inspection take place?
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Priority */}
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold">Priority *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Low">Low</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="High">High</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          Inspection priority level
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Assigned To */}
                <FormField
                  control={form.control}
                  name="assignedTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Assigned To *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter inspector name or ID" 
                          className="bg-background"
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Person responsible for conducting the inspection
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Notes */}
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add any special instructions or notes for the inspection..."
                          className="min-h-[100px] bg-background"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Optional notes or special instructions for the inspector
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </div>

        <SheetFooter className="border-t bg-muted/50 px-6 py-4 mt-auto">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="schedule-inspection-form"
              className="flex-1"
            >
              Schedule Inspection
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
